import { Links } from "@/constants";
import type { HeadsetAudience } from "@/v2-components/pages/games/CompatibleVRHeadsetsSheets";

/**
 * VR Games / Software 개편 (#5475 · 시안 `29889:102282` 데스크탑 · `30035:23192` 모바일).
 *
 * 한 페이지에 **플레이어 셋**을 담는다. 「VR Games」 하위인데 PC Player 가 있는 이유는
 * PC VR(SteamVR 헤드셋)도 VR 범주이기 때문이다.
 *
 * ## ★문구는 **두 폭이 같다** — 폭별 필드를 두지 않는다 (시안 개정 2026-09-03/04)
 *
 * 초판 시안은 모바일·데스크탑의 문장이 갈려서 `descriptionMobile`·`captionMobile`
 * 두 필드를 뒀는데, 개정판은 **글자 단위로 같아졌다.** 둘 다 걷었다.
 *
 * ```
 * 배너·히어로 설명   개정 시안에서 동일 → `description` 하나            (2026-09-03)
 * 카드 부제         3/3 동일           → `caption` 하나                 (2026-09-04)
 * ```
 *
 * 갈리는 것은 **타이포와 배치**뿐이고 그건 컴포넌트의 `laptop:` 이 처리한다.
 * 값이 같은 폭별 필드는 판별력이 0 이라, 되살리면 한쪽만 고치고 다른 쪽을 잊는
 * 자리가 된다 — 가드가 두 이름을 `not.toMatch` 로 막는다.
 *
 * ⚠️ 초판이 채택 근거로 적어 둔 「데스크탑 셋 다 `PC Player 보기` = 목업 복붙」은
 * 여전히 유효하다(카드 CTA 는 `cta` 필드가 갖는다).
 */
export type PlayerId = "pc" | "vr" | "mobile";

export interface StoreEntry {
  /** 표시 이름. 번역 키이자 영문 표시문(리포 관례). */
  name: string;
  /** 부제. 없으면 한 줄로 그린다(VR/Mobile 스토어 행이 그렇다). */
  caption?: string;
  href: string;
}

/**
 * 스토어 행의 우측 표기 — ★**데스크탑 전용이다.**
 *
 * ```
 *          데스크탑              모바일
 *   PC     Download 버튼         Download 버튼
 *   VR     외부링크 아이콘(↗)     ★Download 버튼
 *   Mobile 외부링크 아이콘(↗)     ★Download 버튼
 * ```
 *
 * 모바일은 **셋 다 버튼**이다(시안 실측: `btn / Download` 인스턴스가 7개 행 전부에
 * 붙어 있다 — `30035:23237`·`23244`·`23263`·`23269`·`23275`·`23294`·`23301`).
 * 한 행에만 있으면 목업 잔재로 읽었겠지만 7/7 이라 시안대로 따른다.
 *
 * 그래서 이 값은 **데스크탑 분기에서만** 읽는다.
 */
export type StoreAction = "download" | "external";

export interface PlayerSection {
  id: PlayerId;
  /** 앵커 — 히어로 카드가 여기로 스크롤한다. */
  anchor: string;
  /**
   * 섹션 제목 — ★**플레이어 이름이 아니라 「기기 범주」다**(시안 개정 2026-09-03).
   *
   * ```
   *        종전 구현        개정 시안
   *   PC   PC Player       PC VR
   *   VR   VR Player       Standalone VR
   *   Mob  Mobile Player   PS VR2
   * ```
   *
   * 히어로 카드(`DEVICE_CARDS`)와 **같은 이름**이라 카드를 눌러 내려온 사용자가
   * 같은 낱말을 만난다. 플레이어 이름은 바로 아래 「Download bHaptics Player」와
   * 스토어 행이 담당한다.
   *
   * ⚠️ 시안 두 자리가 어긋난다 — 오타 `Standlone VR`(`29889:102310` 옆 배너 ·
   * `30175:156302`)과 `For PS VR2`(전치사가 셋 중 하나에만 붙는다), 그리고
   * 모바일 PC 만 `PC Player` 로 남아 있다(6칸 중 1칸). **작성자 결정으로 기기
   * 범주 형태로 통일**했다(2026-09-03).
   */
  title: string;
  /**
   * 배너·히어로 설명 — ★**두 폭이 같은 문장이다**(시안 개정 2026-09-03).
   *
   * 종전엔 폭마다 다른 문장이라 `descriptionMobile` 을 따로 뒀는데, 개정판은
   * 데스크탑 배너(래스터)와 모바일 히어로(`30175:156296`·`156303`·`156312`)의
   * 문장이 **글자 단위로 같다.** 그래서 그 필드를 걷었다.
   */
  description: string;
  banner: { desktop: string; mobile: string };
  /** 지원 기기 설명. `null` 이면 그 블록을 안 그린다. */
  /**
   * `showHeadsetSheet` 는 「호환 헤드셋 전체 보기」 트리거다.
   * ★경로가 아니라 **시트**다 — 기존 `CompatibleVrHeadsetsSheets` 를 연다(작성자 결정 A).
   * 초판은 `moreHref: "/compatibility"` 였는데 그 경로는 **307 → `/games`** 로 빠진다
   * (실측). 라우트 존재를 확인하지 않고 그럴듯한 경로를 적은 것이었다.
   */
  /**
   * 배너 아래 「지원 기기」 블록. ★**PC 섹션에는 없다** — 시안 개정으로 빠지고
   * 그 트리거만 `bannerHeadsetCta` 로 배너에 올라갔다(2026-09-07).
   */
  support?: {
    title: string;
    description?: string;
    /**
     * ★**모바일에서 이 블록을 통째로 숨긴다** — VR 만 그렇다.
     *
     * 시안 개정판의 모바일 VR support(`30035:23250`)는 `hidden=true` 이고, 그 안에
     * 남아 있던 내용도 한국어 목업 문장이었다. 데스크탑 VR 은 제목만 두고 그 아래
     * HMD 3종 이미지 행이 정보를 담는데(`29889:156186` 아래 `29889:102367`),
     * 그 행은 **데스크탑 전용**이라 모바일엔 실을 것이 없다 —
     * 히어로 설명이 이미 「Meta Quest, PICO, and HTC VIVE」를 말한다.
     */
    mobileHidden?: boolean;
  } | null;
  download: { title: string; description: string };
  stores: StoreEntry[];
  storeAction: StoreAction;
  /** 「호환 헤드셋 전체 보기」 시트에서 보여줄 브랜드 범위. */
  headsetAudience: HeadsetAudience;
  /**
   * 배너 글자색 — **데스크탑에만 적용된다.**
   * Mobile Player 의 데스크탑 배너만 밝아서 글자가 `#121619` 다(`29889:102409`).
   * ★모바일 배너는 **셋 다 어두워서 흰 글자**다(`30035:23278` — `text-white` + `#C1C7CD`).
   * 그래서 이 값을 두 폭에 다 걸면 모바일이 틀린다.
   */
  bannerTextDesktop: "light" | "dark";
  /**
   * 배너 카피 아래 **파란 알약 버튼** — 「호환 헤드셋 전체 보기」 시트를 연다.
   * 종전 `support.showHeadsetSheet` 가 여기로 옮겨왔다(시안 개정 2026-09-07).
   * 지금은 PC 섹션만 쓴다.
   */
  bannerHeadsetCta?: boolean;
  /**
   * 지원 기기 제품 행 — VR Player 에만 있다(시안 `29889:102365`).
   * 스크린샷에 HMD 3종 + 모델명이 그려져 있는데 초판에서 통째로 빠뜨렸다.
   */
  devices?: { platform: string; models: string; image: string }[];
}

/**
 * ★**시안 자산을 그대로 받아 쓴다.** 초판은 리포에 있던 **다른 페이지 이미지**를 이름만 보고
 * 갖다 썼고(`software_pcplayer_bg` 등) 시안과 하나도 안 맞았다. Figma 자산 URL 은 7일 만료라
 * `public/images/software/vr-software/` 에 내려받아 커밋했다.
 */
const IMG = "/images/software/vr-software";

/**
 * 다운로드 링크는 **전부 기존 상수**다 — 새로 만들지 않았다.
 * macOS 도 `usePCPLatestRelease()` 가 이미 주고 있어서 그 값을 그대로 쓴다.
 */
export const PC_DOWNLOADS = {
  win: "https://sdk-apis.bhaptics.com/api/v1/release-files/download-latest-by-application-name?name=bhaptics-player-win-beta",
  mac: "https://sdk-apis.bhaptics.com/api/v1/release-files/download-latest-by-application-name?name=bhaptics-player-mac-beta",
} as const;

export const PLAYER_SECTIONS: PlayerSection[] = [
  {
    id: "pc",
    anchor: "sw-pc-player",
    /**
     * ★**배너 제목은 `For …` 다** (시안 `29889:102329` · `30255:56188` · `30255:56196`).
     * 히어로의 기기 선택 카드(`DEVICE_CARDS`)는 접두 없이 `PC VR` 이다 — 두 자리가
     * 같은 단어를 쓰지만 **다른 물건**이라 여기만 바꾼다.
     *
     * ⚠️ 모바일 시안은 `For PC Player` 로 명사가 다른데(`30175:156295`) **안 따랐다** —
     * 폭별 제목 필드를 다시 만들지 않고 한 벌로 간다(작성자 확정 2026-09-07).
     * `descriptionMobile`·`captionMobile` 을 걷어낸 결정과 같은 방향이다.
     */
    title: "For PC VR",
    description:
      "Connect TactSuit and bHaptics devices for SteamVR-based VR games. Manage device connection and haptic settings from your PC.",
    banner: {
      desktop: `${IMG}/band-pc-desktop.png`,
      mobile: `${IMG}/band-pc-mobile.jpg`,
    },
    download: {
      title: "Download bHaptics Player",
      description: "Download the installer for your operating system.",
    },
    stores: [
      { name: "Windows", caption: "x64 or later", href: PC_DOWNLOADS.win },
      {
        name: "macOS",
        caption: "Apple Silicon or later",
        href: PC_DOWNLOADS.mac,
      },
    ],
    storeAction: "download",
    headsetAudience: "pcvr",
    bannerTextDesktop: "light",
    /**
     * ★**「호환 헤드셋 전체 보기」가 배너로 올라왔다** (시안 `29889:102326` 데스크탑 ·
     * `30175:156298` 모바일, 2026-09-07).
     *
     * 종전엔 `support` 블록(제목 `Supported Devices` + 설명 + 링크)이 배너 아래 있었고
     * 이 트리거가 거기 있었다. 개정 시안은 **그 블록을 통째로 숨기고**(데스크탑
     * `29889:102331` · 모바일 `30035:23223` 둘 다 `hidden="true"`) 배너 카피 아래
     * **파란 알약 버튼**으로 옮겼다. 링크가 아니라 **버튼**이고 여는 것은 종전과 같은
     * 헤드셋 시트다.
     *
     * ⚠️ **PC 섹션만이다.** VR 섹션(`29889:102365`)은 `Supported Devices` 제목과
     * HMD 3종 행이 **그대로 보인다** — 같이 지우면 안 된다.
     */
    bannerHeadsetCta: true,
  },
  {
    id: "vr",
    anchor: "sw-vr-player",
    /**
     * ⚠️ 시안은 **`For Standlone VR`**(`a` 누락)이다. 제품명 표기는 `Standalone` 이
     * 맞아 **철자를 고쳐 쓴다**(작성자 확정 2026-09-07). 시안 설명에도
     * `PICO, a nd HTC VIVE` 로 띄어쓰기 오타가 있어 그쪽도 안 따랐다.
     */
    title: "For Standalone VR",
    description:
      "Connect bHaptics devices on Meta Quest, PICO, and HTC VIVE. Manage device connection, game discovery, and haptic settings right inside your headset.",
    banner: {
      desktop: `${IMG}/band-vr-desktop.png`,
      mobile: `${IMG}/band-vr-mobile.jpg`,
    },
    /**
     * ★VR 은 **제목만**이다 — 시안 `29889:102365`(`Frame 2147238843` h=244)의 자식은
     * `text 「지원 기기」 h=28` + `Frame 2147238842`(HMD 3개) 둘뿐이다.
     * 설명 문구도, 「호환 헤드셋 전체 보기」 링크도 없다 — 그건 PC 섹션
     * (`29889:102331` 의 `dev-txt` + `Frame 2147238844`)에만 있다.
     * 초판은 세 섹션에 같은 `support` 를 넣어 PC 것이 VR 로 복사됐다.
     */
    support: { title: "Supported Devices", mobileHidden: true },
    download: {
      title: "Download bHaptics Player",
      description: "Download the installer for your headset model.",
    },
    stores: [
      { name: "Meta Store", href: Links.BhapticsMetaQuest },
      { name: "PICO Store", href: Links.VRPPicoStore },
      { name: "VIVEPORT", href: Links.VRPVivePort },
    ],
    storeAction: "external",
    headsetAudience: "standalone",
    bannerTextDesktop: "light",
    // 모델명은 종전 `VrpSupportedHeadsetsSection` 의 `VRP_HEADSETS` 와 같은 값이다
    // (그 섹션은 개편 뒤 렌더 경로가 없어 2026-10-01 에 지웠다).
    devices: [
      {
        platform: "Meta Quest",
        models: "Quest 2 · 3 · 3S · Pro",
        image: `${IMG}/hmd-quest-2x.png`,
      },
      {
        platform: "PICO",
        models: "PICO 4 · 4 Pro · 4 Ultra",
        image: `${IMG}/hmd-pico-2x.png`,
      },
      {
        platform: "HTC VIVE",
        models: "Focus 3 · XR Elite · Focus Vision",
        image: `${IMG}/hmd-vive-2x.png`,
      },
    ],
  },
  {
    id: "mobile",
    anchor: "sw-mobile-player",
    title: "For PS VR2",
    description:
      "Connect and manage bHaptics devices for PS VR2 through the bHaptics Player mobile app.",
    banner: {
      desktop: `${IMG}/band-mobile-desktop.png`,
      mobile: `${IMG}/band-mobile-mobile.jpg`,
    },
    download: {
      title: "Download bHaptics Player",
      description: "Download the installer for your operating system.",
    },
    stores: [
      { name: "App Store", href: Links.BhapticsIOS },
      { name: "Google Play", href: Links.BhapticsAndroid },
    ],
    storeAction: "external",
    headsetAudience: "psvr",
    bannerTextDesktop: "dark",
  },
];

/**
 * 히어로의 기기 선택 카드. 순서·문구는 시안 `29889:102294` · `30035:23203`.
 *
 * ★**부제는 두 폭이 같은 문장이다** — `captionMobile` 을 걷었다(2026-09-04).
 *
 * 초판 주석은 *"부제가 폭마다 진짜로 다르다"* 였는데, 개정 시안을 다시 떠서 세 카드를
 * 대조하니 **3/3 이 글자 단위로 같았다**(모바일 `30035:23204`·`23209`·`23214` 는
 * 텍스트가 래스터에 구워져 있어 스크린샷으로 확인). 값이 같은 필드는 폭을 가르지
 * 못하므로 판별력이 0 이고, `descriptionMobile` 을 걷은 것과 같은 이유로 지웠다.
 *
 * 두 폭이 갈리는 것은 **타이포뿐**이다(모바일 12/16 cg-20 · 데스크탑 14/18
 * SemiBold white/86) — 그건 `sw-head-band` 의 `laptop:` 이 처리한다.
 */
export const DEVICE_CARDS = [
  {
    title: "PC VR",
    caption: "SteamVR-based headsets",
    cta: "View PC Player",
    anchor: "sw-pc-player",
    image: {
      desktop: `${IMG}/card-pcvr.png`,
      mobile: `${IMG}/card-pcvr-mobile.png`,
    },
  },
  {
    title: "Standalone VR",
    caption: "Meta Quest · PICO · HTC VIVE",
    cta: "View VR Player",
    anchor: "sw-vr-player",
    image: {
      desktop: `${IMG}/card-standalone.png`,
      mobile: `${IMG}/card-standalone-mobile.png`,
    },
  },
  {
    title: "PS VR2",
    caption: "PlayStation VR2 · smartphone connection",
    cta: "View Mobile Player",
    anchor: "sw-mobile-player",
    image: {
      desktop: `${IMG}/card-psvr2.png`,
      mobile: `${IMG}/card-psvr2-mobile.png`,
    },
  },
] as const;
